import { UPLOAD_LIMITS, type UploadIntentRequest, type UploadIntentResponse, type UploadPurpose, type UploadView } from "@townsy/contracts";
import { isApiError } from "../api/client";

/**
 * Image upload as a small, dependency-injected state machine (ADR-0009 #3, docs/08 section 5):
 *
 *   idle -> checking -(local refusal)-> rejected_local
 *                    -> requesting (POST /uploads/intents) -> uploading (PUT bytes, progress)
 *                    -> finalizing (POST /uploads/:id/finalize) -> processing (poll GET /uploads/:id)
 *                    -> ready | rejected | expired          any network/API failure -> failed
 *
 * The browser's checks (type by magic bytes, size) only save the visitor a wasted upload: the
 * server sniffs, decodes with pixel limits, strips metadata and re-encodes, and its answer is the
 * one shown. Nothing uploaded is public before a person reviews it.
 */

export const ALLOWED_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export type AllowedUploadType = (typeof ALLOWED_UPLOAD_TYPES)[number];
export const UPLOAD_ACCEPT = ALLOWED_UPLOAD_TYPES.join(",");

export type UploadPhase =
  | "idle"
  | "checking"
  | "rejected_local"
  | "requesting"
  | "uploading"
  | "finalizing"
  | "processing"
  | "ready"
  | "rejected"
  | "expired"
  | "failed"
  | "canceled";

export interface UploadState {
  phase: UploadPhase;
  purpose: UploadPurpose;
  fileName: string | null;
  fileSize: number;
  contentType: AllowedUploadType | null;
  /** 0..1 while the bytes are being sent. */
  progress: number;
  uploadId: string | null;
  /** The server's latest view of the upload (status, reason, private preview, size). */
  view: UploadView | null;
  /** Plain-language explanation of the current phase (refusals, failures). */
  message: string | null;
  /** A failed step can be tried again with the same file. */
  retryable: boolean;
}

export const initialUploadState = (purpose: UploadPurpose): UploadState => ({
  phase: "idle",
  purpose,
  fileName: null,
  fileSize: 0,
  contentType: null,
  progress: 0,
  uploadId: null,
  view: null,
  message: null,
  retryable: false,
});

/** Busy phases: the creative cannot be submitted and the file picker is locked. */
export const UPLOAD_BUSY: ReadonlySet<UploadPhase> = new Set(["checking", "requesting", "uploading", "finalizing", "processing"]);

export const PURPOSE_LABEL: Record<UploadPurpose, string> = { logo: "logo", billboard: "billboard image" };

export function formatBytes(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)} MB`;
  if (n >= 1000) return `${Math.round(n / 1000)} KB`;
  return `${n} bytes`;
}

/** The real image type from the first bytes (PNG, JPEG or WebP), whatever the file name says. */
export function sniffImageType(head: Uint8Array): AllowedUploadType | null {
  const b = head;
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return "image/png";
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return "image/webp";
  return null;
}

export type LocalCheck = { ok: true; contentType: AllowedUploadType } | { ok: false; message: string };

/** The browser-side refusal reasons (type by content, empty file, size limit per purpose). */
export function checkFileLocally(file: { name: string; size: number }, head: Uint8Array, purpose: UploadPurpose): LocalCheck {
  const limit = UPLOAD_LIMITS[purpose];
  if (file.size <= 0) return { ok: false, message: "This file is empty." };
  const type = sniffImageType(head);
  if (!type) {
    return {
      ok: false,
      message: "Only PNG, JPEG or WebP images can be uploaded, and this file is not one of them (whatever its name says). SVG, HTML and other files are refused.",
    };
  }
  if (file.size > limit.maxBytes) {
    return { ok: false, message: `This file is ${formatBytes(file.size)}; a ${PURPOSE_LABEL[purpose]} can be at most ${formatBytes(limit.maxBytes)}.` };
  }
  return { ok: true, contentType: type };
}

/** Plain-language text for a failed upload step; the server's own reason wins for refusals. */
export function uploadErrorMessage(err: unknown): { message: string; retryable: boolean } {
  if (!isApiError(err)) return { message: "The upload was interrupted. Check your connection and try again.", retryable: true };
  switch (err.code) {
    case "UPLOAD_REJECTED":
      return { message: err.message || "Townsy could not accept this image.", retryable: false };
    case "UNAUTHENTICATED":
      return { message: "Sign in again to upload. Your session may have expired.", retryable: false };
    case "EMAIL_NOT_VERIFIED":
      return { message: "Verify your email address before uploading images.", retryable: false };
    case "FORBIDDEN":
    case "BANNED":
      return { message: "Your account can't upload images right now.", retryable: false };
    case "FEATURE_DISABLED":
      return { message: "Image uploads are switched off right now. You can still submit a text-only design.", retryable: false };
    case "RATE_LIMITED":
      return { message: "Too many uploads in a short time. Wait a minute, then try again.", retryable: true };
    case "NOT_FOUND":
      return { message: "This upload slot no longer exists. Upload the file again.", retryable: true };
    case "VALIDATION_FAILED":
      return { message: `The upload was not accepted: ${err.message || "invalid request"}.`, retryable: false };
    default:
      return { message: err.retryable ? "Townsy couldn't take the upload right now. Try again." : err.message || "The upload failed.", retryable: err.retryable || err.status >= 500 };
  }
}

export interface UploadDeps {
  intent(body: UploadIntentRequest): Promise<UploadIntentResponse>;
  put(uploadUrl: string, file: Blob, contentType: string, onProgress: (p: { loaded: number; total: number }) => void, signal: AbortSignal): Promise<void>;
  finalize(uploadId: string): Promise<UploadView>;
  get(uploadId: string, signal: AbortSignal): Promise<UploadView>;
  /** First bytes of the file (for the type check). */
  readHead(file: Blob, bytes: number): Promise<Uint8Array>;
  /** Refuses any upload URL that is not this origin's `/api/v1/uploads/<id>/content`. */
  isSafeUploadUrl(uploadUrl: string, uploadId: string): boolean;
  sleep(ms: number, signal: AbortSignal): Promise<void>;
  onChange(state: UploadState): void;
  /** Total time to wait for the server's image check before offering "Check again" (default 90 s). */
  processingLimitMs?: number;
  now?(): number;
}

/** Poll delays while the server checks the image (ms). */
export const PROCESSING_POLL_MS = [500, 1000, 1500, 2000, 3000, 4000, 5000] as const;

type NamedBlob = Blob & { name?: string };

export class UploadController {
  private s: UploadState;
  private gen = 0;
  private abort: AbortController | null = null;
  private file: NamedBlob | null = null;

  constructor(
    purpose: UploadPurpose,
    private readonly deps: UploadDeps,
  ) {
    this.s = initialUploadState(purpose);
  }

  get state(): UploadState {
    return this.s;
  }

  private set(patch: Partial<UploadState>): void {
    this.s = { ...this.s, ...patch };
    this.deps.onChange(this.s);
  }

  /** Forget the current upload (e.g. switching to a text-only design). */
  reset(purpose: UploadPurpose = this.s.purpose): void {
    this.gen++;
    this.abort?.abort();
    this.abort = null;
    this.file = null;
    this.s = initialUploadState(purpose);
    this.deps.onChange(this.s);
  }

  cancel(): void {
    if (!UPLOAD_BUSY.has(this.s.phase)) return;
    this.gen++;
    this.abort?.abort();
    this.abort = null;
    this.set({ phase: "canceled", progress: 0, message: "Upload canceled. Nothing was submitted.", retryable: true });
  }

  /** Check the file in the browser, then send it and wait for the server's verdict. */
  async start(file: NamedBlob): Promise<void> {
    const gen = ++this.gen;
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    this.file = file;
    const purpose = this.s.purpose;
    this.s = { ...initialUploadState(purpose), phase: "checking", fileName: file.name ?? null, fileSize: file.size };
    this.deps.onChange(this.s);
    let head: Uint8Array;
    try {
      head = await this.deps.readHead(file, 16);
    } catch {
      if (gen !== this.gen) return;
      this.set({ phase: "rejected_local", message: "This file could not be read. Choose it again.", retryable: false });
      return;
    }
    if (gen !== this.gen) return;
    const local = checkFileLocally({ name: file.name ?? "", size: file.size }, head, purpose);
    if (!local.ok) {
      this.set({ phase: "rejected_local", message: local.message, retryable: false });
      return;
    }
    this.set({ phase: "requesting", contentType: local.contentType });
    let intent: UploadIntentResponse;
    try {
      intent = await this.deps.intent({ purpose, contentType: local.contentType, byteSize: file.size });
    } catch (err) {
      return this.fail(gen, err);
    }
    if (gen !== this.gen) return;
    if (!this.deps.isSafeUploadUrl(intent.uploadUrl, intent.uploadId)) {
      this.set({ phase: "failed", uploadId: intent.uploadId, message: "The server answered with an unexpected upload address, so nothing was sent.", retryable: false });
      return;
    }
    if (file.size > intent.maxBytes) {
      this.set({ phase: "rejected_local", uploadId: intent.uploadId, message: `This file is ${formatBytes(file.size)}; the limit is ${formatBytes(intent.maxBytes)}.`, retryable: false });
      return;
    }
    this.set({ phase: "uploading", uploadId: intent.uploadId, progress: 0 });
    try {
      await this.deps.put(
        intent.uploadUrl,
        file,
        local.contentType,
        (p) => {
          if (gen !== this.gen) return;
          const total = p.total > 0 ? p.total : file.size;
          const progress = total > 0 ? Math.min(1, Math.max(0, p.loaded / total)) : 0;
          if (progress !== this.s.progress) this.set({ progress });
        },
        abort.signal,
      );
    } catch (err) {
      return this.fail(gen, err);
    }
    if (gen !== this.gen) return;
    this.set({ phase: "finalizing", progress: 1 });
    let view: UploadView;
    try {
      view = await this.deps.finalize(intent.uploadId);
    } catch (err) {
      return this.fail(gen, err);
    }
    if (gen !== this.gen) return;
    await this.settle(gen, view, abort.signal);
  }

  /** After a failure: check again (still processing on the server) or send the same file again. */
  async retry(): Promise<void> {
    if (this.s.phase === "failed" && this.s.uploadId && this.s.view?.status === "processing") {
      const gen = ++this.gen;
      const abort = new AbortController();
      this.abort = abort;
      this.set({ phase: "processing", message: null, retryable: false });
      await this.settle(gen, this.s.view, abort.signal);
      return;
    }
    if (this.file) await this.start(this.file);
  }

  private async settle(gen: number, first: UploadView, signal: AbortSignal): Promise<void> {
    const limit = this.deps.processingLimitMs ?? 90_000;
    const now = this.deps.now ?? Date.now;
    const started = now();
    let view = first;
    for (let attempt = 0; ; attempt++) {
      if (gen !== this.gen) return;
      if (view.status !== "processing" && view.status !== "awaiting_content") break;
      this.set({ phase: "processing", view, message: null });
      if (view.status === "awaiting_content" && attempt > 2) {
        this.set({ phase: "failed", view, message: "The server did not receive the file. Upload it again.", retryable: true });
        return;
      }
      if (now() - started >= limit) {
        this.set({ phase: "failed", view, message: "Checking the image is taking longer than usual. Check again in a moment.", retryable: true });
        return;
      }
      try {
        await this.deps.sleep(PROCESSING_POLL_MS[Math.min(attempt, PROCESSING_POLL_MS.length - 1)]!, signal);
        if (gen !== this.gen) return;
        view = await this.deps.get(view.uploadId, signal);
      } catch (err) {
        return this.fail(gen, err);
      }
    }
    if (gen !== this.gen) return;
    switch (view.status) {
      case "ready":
        this.set({ phase: "ready", view, message: null, retryable: false });
        return;
      case "rejected":
        this.set({ phase: "rejected", view, message: view.reason ?? "Townsy could not accept this image.", retryable: false });
        return;
      case "expired":
        this.set({ phase: "expired", view, message: "The upload slot expired before it finished. Upload the file again.", retryable: true });
        return;
      default:
        this.set({ phase: "failed", view, message: "Unexpected upload state. Upload the file again.", retryable: true });
    }
  }

  private fail(gen: number, err: unknown): void {
    if (gen !== this.gen) return;
    if (err instanceof DOMException && err.name === "AbortError") return;
    const { message, retryable } = uploadErrorMessage(err);
    const rejected = isApiError(err) && err.code === "UPLOAD_REJECTED";
    this.set({ phase: rejected ? "rejected" : "failed", message, retryable });
  }
}

/** Short status line for the upload field. */
export function uploadStatusText(s: UploadState): string {
  const what = PURPOSE_LABEL[s.purpose];
  switch (s.phase) {
    case "idle":
      return `No ${what} chosen yet. PNG, JPEG or WebP, at most ${formatBytes(UPLOAD_LIMITS[s.purpose].maxBytes)} and ${UPLOAD_LIMITS[s.purpose].maxPixels} px on the longest side.`;
    case "checking":
      return "Checking the file…";
    case "requesting":
      return "Preparing a private upload slot…";
    case "uploading":
      return `Uploading… ${Math.round(s.progress * 100)}%`;
    case "finalizing":
      return "Upload complete. Handing it to Townsy's image check…";
    case "processing":
      return "Townsy is checking and re-encoding the image (metadata removed)…";
    case "ready":
      return `Checked and ready${s.view?.width && s.view?.height ? ` (${s.view.width} × ${s.view.height} px)` : ""}. Only you and Townsy's reviewers can see it until it is approved.`;
    case "rejected":
    case "rejected_local":
    case "expired":
    case "failed":
    case "canceled":
      return s.message ?? "The upload did not complete.";
  }
}
